package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.{IdentityData, UserDataRest}
import com.neurobs.ccab.db.{LanguageData, TranslatorData}
import com.neurobs.ccab.translation.TranslatorDataView
import com.neurobs.js.ccab.{CCABServer, RelativeJSCCABServer, ServerContext, TranslationServer}
import com.neurobs.ccab.{CCABServer => CCABServerNew}
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{BooleanAttribute, GenericState, GenericStateGenerator, ServerDataAttribute, StateAttribute}
import com.neurobs.util.{Guid, SecondsTimestamp}
import org.scalajs.dom.console
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

import scala.concurrent.ExecutionContext.Implicits.global

case class TranslatorState
(
	server: CCABServerNew,
	validUserData: IdentityData,
	otherUsersState: ServerDataAttribute[List[UserDataRest]],
	languagesState: ServerDataAttribute[List[LanguageData]],
	selectedUserState: StateAttribute[Option[UserDataRest]],
	selectedTranslatorState: StateAttribute[Option[TranslatorDataView]],
	registeredTranslatorsState: ServerDataAttribute[List[TranslatorDataView]],
	addingTranslatorState: BooleanAttribute,
	loadingTranslatorCreateState: BooleanAttribute,
                     ) extends GenericState {


  def userId = selectedUserState.value.map(_.id).getOrElse(validUserData.legacyUserId)

  def languages = languagesState.value
  def setLanguages(languages: List[LanguageData]) = languagesState.setData(languages)
  def unregisteredLanguages = languages match {
    case Left(_) => List()
    case Right(languageChoices) =>
      languageChoices.filter(choice => {
        registeredTranslators match {
          case Left(_) => true
          case Right(existingTranslators) => !existingTranslators.exists(_.languageId == choice.id)
        }
      })
  }

  def selectedTranslator = selectedTranslatorState.value
  def setSelectedTranslator(translatorData: TranslatorDataView) = setOptionState(selectedTranslatorState, translatorData)
  def clearSelectedTranslator() = voidState(selectedTranslatorState)

  def registeredTranslators = registeredTranslatorsState.value
  def setRegisteredTranslators(registeredTranslators: List[TranslatorDataView]) = registeredTranslatorsState.setData(registeredTranslators)
  def clearRegisteredTranslators() = registeredTranslatorsState.reset()

  def addNewTranslator(translatorData: TranslatorData, languageData: LanguageData) = {
    registeredTranslators match {
      case Left(_) =>
      case Right(existingTranslators) =>
        val newTranslatorView = TranslatorDataView(translatorData.id, translatorData.userId, "", translatorData.languageId, languageData.displayName)
        setRegisteredTranslators(existingTranslators :+ newTranslatorView)
        setSelectedTranslator(newTranslatorView)
    }
  }

  def addingTranslator = addingTranslatorState.value
  def openTranslatorRegistration() = addingTranslatorState.setValue(true)
  def closeTranslatorRegistration() = addingTranslatorState.setValue(false)

  def awaitingServerTranslators = registeredTranslatorsState.loading
  def awaitingServerLanguages = languagesState.loading
  def awaitingServerTranslatorCreate = loadingTranslatorCreateState.value

  def loadServerLanguages() = {
    TranslationServer.getAllLanguages(handleServerLanguages)
  }

  def handleServerLanguages = ServerResponseUtil.handleObjectResultResponse[Seq[LanguageData]](
    handleObject = data => setLanguages(data.toList),
    onAnyError = languagesState.setError(_)
  )

  def loadServerTranslators() = {
    TranslationServer.getTranslatorsForUser(userId)(handleServerTranslators)
  }

  def handleServerTranslators = ServerResponseUtil.handleObjectResultResponse[Seq[TranslatorDataView]](
    handleObject = data => setRegisteredTranslators(data.toList),
    onAnyError = languagesState.setError(_)
  )

  def loadServerUsers() = {
    if (validUserData.isSiteAdmin) {
		 server.getUsers(validUserData.legacyAccountId)._2 map {
			 case Right(data) => otherUsersState.setData(data.toList.filterNot(_.id == validUserData.legacyUserId))
			 case Left(error) => otherUsersState.setError(error.description)
		 }
	 }
  }

  def registerTranslator(languageData: LanguageData): Unit = {
    loadingTranslatorCreateState.setValue(true)
    val translatorData = TranslatorData(0, SecondsTimestamp.now(), userId, languageData.id, true)
    TranslationServer.createTranslator(translatorData)(handleServerTranslatorCreate(languageData))
  }

  def handleServerTranslatorCreate(languageData: LanguageData) = ServerResponseUtil.handleObjectResultResponse[TranslatorData](
    handleObject = translatorData => addNewTranslator(translatorData, languageData),
    onAnyResponse = () => loadingTranslatorCreateState.setValue(false)
  )
}

object TranslatorStateGenerator extends GenericStateGenerator[TranslatorState] {

  override def generateEmptyDefaultState(): TranslatorState = {
    TranslatorState(
		 RelativeJSCCABServer(),
		 IdentityData(Guid.nullGuid, Guid.nullGuid, "", "", "", 0, 0, Seq()),
      emptyServerDataState[List[UserDataRest]](),
      emptyServerDataState[List[LanguageData]](),
      emptyOptionDefaultState[UserDataRest](),
      emptyOptionDefaultState[TranslatorDataView](),
      emptyServerDataState[List[TranslatorDataView]](),
      emptyBooleanState(),
      emptyBooleanState()
    )
  }
}

@react
object TranslatorStateProvider {

  case class Props(userData: IdentityData, children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    val selectedTranslatorState = StateAttribute(useState[Option[TranslatorDataView]](None))
    val otherUsersState = ServerDataAttribute(useState[Either[Option[String], List[UserDataRest]]](Left(None)))
    val languagesState = ServerDataAttribute(useState[Either[Option[String], List[LanguageData]]](Left(None)))
    val selectedUserState = StateAttribute(useState[Option[UserDataRest]](None))
    val registeredTranslatorsState = ServerDataAttribute(useState[Either[Option[String], List[TranslatorDataView]]](Left(None)))
    val addingTranslatorState = BooleanAttribute(useState(false))
    val loadingTranslatorCreateState = BooleanAttribute(useState(false))
	  val server = ServerContext.accessState

    val translatorState = TranslatorState(
		 server,
      validUserData = props.userData,
      otherUsersState = otherUsersState,
      languagesState = languagesState,
      selectedUserState = selectedUserState,
      selectedTranslatorState = selectedTranslatorState,
      registeredTranslatorsState = registeredTranslatorsState,
      addingTranslatorState = addingTranslatorState,
      loadingTranslatorCreateState = loadingTranslatorCreateState
    )

    useEffect(() => {
      translatorState.clearSelectedTranslator()
      translatorState.clearRegisteredTranslators()
      translatorState.loadServerTranslators()
    }, Vector(translatorState.selectedUserState.value))

    useEffect(() => {
      translatorState.loadServerUsers()
      translatorState.loadServerLanguages()
      translatorState.loadServerTranslators()
    }, Vector())


    TranslatorStateGenerator.Provider(translatorState)(
      props.children:_*
    )
  }

}
